import { Injectable, inject } from '@angular/core'
import { BaseStorageService } from './base-storage.service'
import { ApiService } from './api.service'
import { BehaviorSubject, map } from 'rxjs'
import { User } from '../modal/person.modal'
import { ApiRegisterUser } from '../modal/api.modal'
import { environment } from '../../environment'
import { decrypt, encrypt, hashMD5 } from '../utils/crypto.util'

const AuthStorageKey = {
  AUTH_TOKEN: hashMD5('AUTH_TOKEN'),
  CURRENT_USER: hashMD5('CURRENT_USER')
}

export enum Role {
  VOLUNTEER = 'volunteer',
  COACH = 'coach'
}

export interface StorageDocument {
  type: string
  name: {
    en: string
    cn: string
    hk: string
  }
  url: string
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private api = inject(ApiService)
  private storage = inject(BaseStorageService)
  private loginSub = new BehaviorSubject(false)
  private token: string | undefined
  private currentUser: User | undefined
  loginChange = this.loginSub.asObservable()

  getCurrentUser() {
    if (!this.currentUser) {
      let storageUser = this.storage.get(AuthStorageKey.CURRENT_USER)
      if (storageUser) {
        this.currentUser = JSON.parse(decrypt(storageUser))
      }
    }
    return this.currentUser
  }

  setCurrentUser(user: User) {
    this.currentUser = user
    this.storage.set(AuthStorageKey.CURRENT_USER, encrypt(JSON.stringify(user)))
  }

  clearCurrentUser() {
    this.currentUser = undefined
    this.storage.remove(AuthStorageKey.CURRENT_USER)
  }

  getToken() {
    if (this.token) {
      return this.token
    }

    let token = this.storage.get(AuthStorageKey.AUTH_TOKEN)

    if (token) {
      this.loginSub.next(true)
      this.token = decrypt(token)
    }

    return this.token
  }

  setToken(token: string) {
    if (this.token === token) {
      return
    }
    this.token = token
    this.storage.set(AuthStorageKey.AUTH_TOKEN, encrypt(token))
    if (token) {
      this.loginSub.next(true)
    } else {
      this.loginSub.next(false)
    }
  }

  clearToken() {
    this.token = undefined
    this.storage.remove(AuthStorageKey.AUTH_TOKEN)
  }

  login(email: string, password: string) {
    return this.api.login(email, password).pipe(
      map((res) => {
        this.setToken(res.token)
        this.setCurrentUser(res.volunteer)
        return true
      })
    )
  }

  isLoggedIn() {
    return !!this.getToken()
  }

  changePassword(currentPwd: string, newPwd: string, confirmPwd: string) {
    return this.api.changePassword(currentPwd, newPwd, confirmPwd).pipe(
      map((res) => {
        return res.status === 'ok'
      })
    )
  }

  logout() {
    return this.api.logout().pipe(
      map((res) => {
        this.clearToken()
        this.clearCurrentUser()
        this.loginSub.next(false)
        return true
      })
    )
  }

  register(user: ApiRegisterUser) {
    user.type = 'volunteer'
    return this.api.register(user).pipe(
      map((res) => {
        return res.status === 'ok'
      })
    )
  }

  getSiteTncDoc() {
    return this.api.getDocuments().pipe(
      map((res) => {
        const doc = res?.data?.documents.find((d) => d.type === 'site_tnc_privacy')
        if (!doc) return null
        return {
          type: doc.type,
          name: { en: doc.name_en, cn: doc.name_zh_cn, hk: doc.name_zh_hk },
          url: environment.baseUrl + '/storage/' + doc.file
        } as StorageDocument
      })
    )
  }

  getConsentDocs() {
    return this.api.getDocuments().pipe(
      map((res) => {
        return res?.data?.documents
          .filter((doc) => {
          switch (doc.type) {
            case 'agree_safeguarding_policy':
            case 'agree_rules_regulation':
            case 'agree_volunteer_guide':
            case 'agree_indemnity_form':
            case 'site_tnc_privacy':
              return true
          }
            return false
          })
          .map((doc) => {
            return {
              type: doc.type,
              name: {
                en: doc.name_en,
                cn: doc.name_zh_cn,
                hk: doc.name_zh_hk
              },
              url: environment.baseUrl + '/storage/' + doc.file
            } as StorageDocument
          })
      })
    )
  }
}
